import { Box } from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';

declare global {
  interface Window {
    turnstile?: {
      render(
        container: HTMLElement,
        options: {
          sitekey: string;
          callback: (token: string) => void;
          theme?: 'auto' | 'light' | 'dark';
          'expired-callback'?: () => void;
          'error-callback'?: () => void;
        },
      ): string;
      remove?: (widgetId: string) => void;
    };
  }
}

interface CaptchaProps {
  siteKey: string;
  onChange(token: string): void;
  onExpire(): void;
}

const SCRIPT_ID = 'cf-turnstile-script';
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

export const Captcha = (props: CaptchaProps): JSX.Element => {
  const { siteKey, onChange, onExpire } = props;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [ready, setReady] = useState<boolean>(false);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (window.turnstile) {
      setReady(true);
      return;
    }

    const existing = document.getElementById(SCRIPT_ID);
    const script = (existing ?? document.createElement('script')) as HTMLScriptElement;
    const onLoad = () => setReady(true);

    if (!existing) {
      script.id = SCRIPT_ID;
      script.src = SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }

    script.addEventListener('load', onLoad);
    return () => {
      script.removeEventListener('load', onLoad);
    };
  }, []);

  useEffect(() => {
    if (!ready || !window.turnstile || containerRef.current === null) {
      return;
    }

    if (widgetIdRef.current !== null && window.turnstile.remove) {
      window.turnstile.remove(widgetIdRef.current);
      widgetIdRef.current = null;
    }

    widgetIdRef.current = window.turnstile.render(containerRef.current, {
      sitekey: siteKey,
      theme: 'auto',
      callback: onChange,
      'expired-callback': onExpire,
      'error-callback': onExpire,
    });

    return () => {
      if (widgetIdRef.current !== null && window.turnstile?.remove) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [ready, siteKey, onChange, onExpire]);

  return <Box ref={containerRef} minH="65px" />;
};

